import React, { useEffect, useState } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../config/api';
import {
  buildFreshUrl,
  CONTENT_SYNC_KEYS,
  NO_CACHE_JSON_HEADERS,
  subscribeToContentUpdates,
} from '../../utils/contentSync';
import { useSiteContent } from '../../context/SiteContentContext';
import {
  getSessionCache,
  removeSessionCache,
  setSessionCache,
} from '../../utils/sessionCache';
import { buildProductPath } from '../../utils/productRoutes';
import { getRichTextPlainText } from '../../utils/richText';
import { getPrimaryProductImage } from '../../utils/productMedia';
import './ProductCategory.css';

import pic1 from '../../Assets/pic1.jpg';
import pic2 from '../../Assets/pic2.jpg';
import pic3 from '../../Assets/pic3.jpg';
import pic4 from '../../Assets/pic4.jpg';
import gauge2 from '../../Assets/gauge2.jpg';
import gauges from '../../Assets/Gauges.jpg';
import pipes from '../../Assets/pipes.jpg';

const fallbackImages = [pic1, pic2, pic3, pic4, gauge2, gauges, pipes];
const PRODUCTS_CACHE_KEY = 'homepage-products-v20260501b';
const PRODUCTS_CACHE_TTL_MS = 10 * 60 * 1000;
const STATIC_PRODUCTS = [
  { _id: 1, name: 'Mini Series Needle Valves', category: 'valves', images: [{ url: pic1 }] },
  { _id: 2, name: 'IB Series Needle Valves', category: 'valves', images: [{ url: pic2 }] },
  { _id: 3, name: 'Magnetic Level Gauges', category: 'pressure gauge', images: [{ url: pic3 }] },
  { _id: 4, name: 'Temperature Sensors', category: 'temperature gauge', images: [{ url: pic4 }] },
  { _id: 5, name: 'Pressure Transmitters', category: 'pressure gauge', images: [{ url: gauge2 }] },
  { _id: 6, name: 'Flow Meters', category: 'regulators', images: [{ url: gauges }] },
  { _id: 7, name: 'Control Valves', category: 'valves', images: [{ url: pipes }] },
];

const getPlainProductText = (value = '') =>
  getRichTextPlainText(value).replace(/\s+/g, ' ').trim();

const getSliderLayout = (viewportWidth = 1440) => {
  if (viewportWidth <= 480) {
    return { cardWidth: 200, gap: 15, containerInset: 40 };
  }

  if (viewportWidth <= 768) {
    return { cardWidth: 220, gap: 20, containerInset: 32 };
  }

  if (viewportWidth <= 1200) {
    return { cardWidth: 250, gap: 30, containerInset: 80 };
  }

  return { cardWidth: 280, gap: 30, containerInset: 120 };
};

const ProductCategory = () => {
  const navigate = useNavigate();
  const { siteContent, isSiteContentLoading } = useSiteContent();

  const [productsData, setProductsData] = useState(null);
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(STATIC_PRODUCTS[0]._id);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [maxIndex, setMaxIndex] = useState(0);
  const [sliderMetrics, setSliderMetrics] = useState(() =>
    getSliderLayout(typeof window !== 'undefined' ? window.innerWidth : 1440)
  );

  const fetchProducts = async () => {
    try {
      const response = await fetch(buildFreshUrl(API_URL.PRODUCTS), {
        cache: 'no-store',
        headers: {
          ...NO_CACHE_JSON_HEADERS,
        },
      });

      const productsResult = await response.json();

      if (!productsResult.success) {
        throw new Error(productsResult.message || 'Invalid products response');
      }

      if (Array.isArray(productsResult.products)) {
        setProductsData(productsResult.products);
        setSessionCache(PRODUCTS_CACHE_KEY, productsResult.products);
        return;
      }

      if (Array.isArray(productsResult.data)) {
        setProductsData(productsResult.data);
        setSessionCache(PRODUCTS_CACHE_KEY, productsResult.data);
        return;
      }

      throw new Error('Invalid response format: products array not found');
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setProductsLoaded(true);
    }
  };

  const sliderIds =
    Array.isArray(siteContent?.home_product_slider) && siteContent.home_product_slider.length > 0
      ? siteContent.home_product_slider.map((id) => String(id))
      : [];

  const resolvedProducts =
    productsData && sliderIds.length > 0
      ? sliderIds
          .map((id) =>
            productsData.find((product) => String(product._id ?? product.id) === id)
          )
          .filter(Boolean)
      : [];

  const apiFallbackProducts =
    Array.isArray(productsData) && productsData.length > 0
      ? productsData.slice(0, Math.min(productsData.length, 8))
      : [];
  const products =
    resolvedProducts.length > 0
      ? resolvedProducts
      : apiFallbackProducts.length > 0
        ? apiFallbackProducts
        : STATIC_PRODUCTS;
  const productSelectionKey = products
    .map((product) => String(product._id ?? product.id))
    .join('|');

  useEffect(() => {
    const cachedProducts = getSessionCache(PRODUCTS_CACHE_KEY, PRODUCTS_CACHE_TTL_MS);

    if (Array.isArray(cachedProducts) && cachedProducts.length > 0) {
      setProductsData(cachedProducts);
      setProductsLoaded(true);
    }

    fetchProducts();
  }, []);

  useEffect(
    () =>
      subscribeToContentUpdates({
        keys: [CONTENT_SYNC_KEYS.PRODUCTS],
        onUpdate: () => {
          removeSessionCache(PRODUCTS_CACHE_KEY);
          fetchProducts();
        },
      }),
    []
  );

  useEffect(() => {
    if (products.length === 0) {
      setSelectedProduct(null);
      return;
    }

    setSelectedProduct((previousSelection) => {
      const hasSelectedProduct = products.some(
        (product) => String(product._id ?? product.id) === String(previousSelection)
      );

      if (hasSelectedProduct) {
        return previousSelection;
      }

      return products[0]._id ?? products[0].id;
    });
  }, [productSelectionKey]);

  useEffect(() => {
    const updateVisibleCount = () => {
      const nextLayout = getSliderLayout(window.innerWidth);
      const containerWidth = Math.max(
        nextLayout.cardWidth,
        window.innerWidth - nextLayout.containerInset
      );
      const count = Math.max(
        1,
        Math.floor((containerWidth + nextLayout.gap) / (nextLayout.cardWidth + nextLayout.gap))
      );
      const calculatedMaxIndex = Math.max(0, products.length - count);

      setSliderMetrics(nextLayout);
      setMaxIndex(calculatedMaxIndex);
    };

    updateVisibleCount();
    window.addEventListener('resize', updateVisibleCount);

    return () => window.removeEventListener('resize', updateVisibleCount);
  }, [products.length]);

  useEffect(() => {
    setCurrentIndex((previousIndex) => Math.min(previousIndex, maxIndex));
  }, [maxIndex]);

  const nextSlide = () => {
    setCurrentIndex((previousIndex) => (previousIndex >= maxIndex ? 0 : previousIndex + 1));
  };

  const prevSlide = () => {
    setCurrentIndex((previousIndex) => (previousIndex <= 0 ? maxIndex : previousIndex - 1));
  };

  const getTransformValue = () => {
    return `translateX(-${(sliderMetrics.cardWidth + sliderMetrics.gap) * currentIndex}px)`;
  };

  const areSliderControlsDisabled = maxIndex === 0;
  const showLoadingState = isSiteContentLoading && !productsLoaded && products.length === 0;

  if (showLoadingState) {
    return (
      <section className="product-category-section">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading featured products...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="product-category-section">
      <div className="container">
        <div className="section-header">
          <h2 className="animated-title">PRODUCT CATEGORY</h2>
          <div className="slider-controls">
            <button
              type="button"
              className="slider-arrow"
              onClick={prevSlide}
              disabled={areSliderControlsDisabled}
              aria-label="Previous products"
            >
              <FaChevronLeft />
            </button>
            <button
              type="button"
              className="slider-arrow"
              onClick={nextSlide}
              disabled={areSliderControlsDisabled}
              aria-label="Next products"
            >
              <FaChevronRight />
            </button>
          </div>
        </div>

        <div className="product-slider-container">
          {products.length > 0 ? (
            <div className="product-slider">
              <div className="product-slider-track" style={{ transform: getTransformValue() }}>
                {products.map((product, index) => {
                  const productId = product._id ?? product.id;
                  const plainProductName = getPlainProductText(product.name);
                  const plainProductLabel =
                    getPlainProductText(product.subcategory) ||
                    String(product.subcategory || '').trim() ||
                    getPlainProductText(product.category) ||
                    String(product.category || '').trim();

                  if (!productId) {
                    return null;
                  }

                  const imageUrl = getPrimaryProductImage(
                    product,
                    fallbackImages[index % fallbackImages.length]
                  );

                  return (
                    <div key={productId} className="product-item">
                      <div
                        className={`product-card ${
                          String(selectedProduct) === String(productId) ? 'selected' : ''
                        }`}
                        onClick={() => {
                          setSelectedProduct(productId);
                          navigate(buildProductPath(product));
                        }}
                      >
                        <div className="product-image">
                          <img
                            src={imageUrl}
                            alt={`${plainProductName || 'Product'} ${plainProductLabel ? `in ${plainProductLabel}` : 'industrial product'}`}
                            loading="lazy"
                            decoding="async"
                            onError={(event) => {
                              event.target.src = fallbackImages[index % fallbackImages.length];
                            }}
                          />
                        </div>
                      </div>
                      <div className="product-name">{plainProductName || 'Product'}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="product-slider">
              <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading featured products...</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ProductCategory;
